import React, { useMemo } from 'react';
import {
  ReactFlow,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './hsv-pyramid-flow.scss';
import { getHsvHoldingStructure } from '../hsvCorpData';

// --- CUSTOM NODES ---

const TriangleNode = () => (
  <div className="pyramid-triangle-node"></div>
);

const DividerNode = () => (
  <div className="pyramid-divider-node"></div>
);

const LabelNode = ({ data }) => (
  <div className="pyramid-label-node">
    {data.name.includes('(') ? (
      <>
        <span>{data.name.split('(')[0]}</span>
        <span className="sub-label">({data.name.split('(')[1]}</span>
      </>
    ) : (
      data.name
    )}
  </div>
);

const DescNode = ({ data }) => (
  <div className="pyramid-desc-node">
    {data.descriptions && data.descriptions.length > 0 && (
      <ul>
        {data.descriptions.map((desc, i) => (
          <li key={i}>{desc}</li>
        ))}
      </ul>
    )}
  </div>
);

const RoleNode = ({ data }) => {
  const { role, color, sizeClass } = data;
  return (
    <div className={`pyramid-role-node color-${color} ${sizeClass}`}>
      <Handle type="target" position={Position.Top} id="top" style={{ background: 'transparent', border: 'none' }} />
      {role}
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: 'transparent', border: 'none' }} />
    </div>
  );
};

const nodeTypes = {
  triangleNode: TriangleNode,
  dividerNode: DividerNode,
  labelNode: LabelNode,
  descNode: DescNode,
  roleNode: RoleNode,
};

// --- REACT FLOW COMPONENT ---

export default function HsvPyramidReactFlow({ isEnglish }) {
  const holdingStruct = getHsvHoldingStructure(isEnglish);
  const layers = holdingStruct?.architecture?.layers || [];

  const { nodes, edges } = useMemo(() => {
    if (layers.length === 0) return { nodes: [], edges: [] };

    const newNodes = [];
    const newEdges = [];

    const CENTER_X = 700;
    
    // Background Triangle
    newNodes.push({
      id: 'bg-triangle',
      type: 'triangleNode',
      position: { x: CENTER_X - 425, y: -20 },
      draggable: false,
      selectable: false,
      data: {}
    });

    // Dividers
    newNodes.push({
      id: 'div-1',
      type: 'dividerNode',
      position: { x: CENTER_X - 425, y: 160 },
      draggable: false,
      selectable: false,
      data: {}
    });
    newNodes.push({
      id: 'div-2',
      type: 'dividerNode',
      position: { x: CENTER_X - 425, y: 480 },
      draggable: false,
      selectable: false,
      data: {}
    });

    // Y coordinates for the 4 layers
    const layerY = [40, 240, 400, 560];

    layers.forEach((layer, idx) => {
      // 1. Label Node (Left)
      newNodes.push({
        id: `label-${idx}`,
        type: 'labelNode',
        position: { x: 0, y: layerY[idx] },
        draggable: false,
        data: { name: layer.name }
      });

      // 2. Desc Node (Right)
      newNodes.push({
        id: `desc-${idx}`,
        type: 'descNode',
        position: { x: 1150, y: layerY[idx] - 30 }, // Moved up by 30px to vertically center with the boxes
        draggable: false,
        data: { descriptions: layer.descriptions }
      });

      // 3. Role Nodes (Center)
      if (idx === 0 || idx === 1) {
        // Single node (Chairman / CEO)
        const roleId = `role-${idx}-0`;
        newNodes.push({
          id: roleId,
          type: 'roleNode',
          position: { x: CENTER_X - 70, y: layerY[idx] }, // width 140 -> offset 70
          draggable: false,
          data: { role: layer.role, color: layer.color, sizeClass: 'single-node' }
        });
      } else if (idx === 2) {
        // Two nodes (Functional Divisions)
        // width 180, gap 40 -> total 400. Offset from center: -200 and +20
        newNodes.push({
          id: `role-2-0`,
          type: 'roleNode',
          position: { x: CENTER_X - 200, y: layerY[idx] },
          draggable: false,
          data: { role: layer.roles[0], color: layer.color, sizeClass: 'multi-node-2' }
        });
        newNodes.push({
          id: `role-2-1`,
          type: 'roleNode',
          position: { x: CENTER_X + 20, y: layerY[idx] },
          draggable: false,
          data: { role: layer.roles[1], color: layer.color, sizeClass: 'multi-node-2' }
        });
      } else if (idx === 3) {
        // Five nodes (Affiliated Companies)
        // width 130, gap 20 -> 150 each step
        // Centers: 335, 485, 635, 785, 935.
        // Positions (top-left): x - 65 (half width)
        const startX = CENTER_X - 365;
        layer.roles.forEach((r, rIdx) => {
          newNodes.push({
            id: `role-3-${rIdx}`,
            type: 'roleNode',
            position: { x: startX + (rIdx * 150), y: layerY[idx] },
            draggable: false,
            data: { role: r, color: layer.color, sizeClass: 'multi-node-5' }
          });
        });
      }
    });

    // Edges (Green Lines)
    const edgeStyle = { stroke: '#4ade80', strokeWidth: 2 }; // Tailwind Green-400
    
    // Chairman -> CEO
    newEdges.push({
      id: 'e-0-1',
      source: 'role-0-0',
      target: 'role-1-0',
      type: 'step',
      style: edgeStyle,
      zIndex: 10, // Ensure edges render above nodes
    });

    // CEO -> Functional (2 nodes)
    newEdges.push({
      id: 'e-1-2-0',
      source: 'role-1-0',
      target: 'role-2-0',
      type: 'step',
      style: edgeStyle,
      zIndex: 10,
    });
    newEdges.push({
      id: 'e-1-2-1',
      source: 'role-1-0',
      target: 'role-2-1',
      type: 'step',
      style: edgeStyle,
      zIndex: 10,
    });

    // Functional -> Companies (5 nodes)
    // Left functional node (2-0) to first 2 companies
    newEdges.push({ id: 'e-2-3-0', source: 'role-2-0', target: 'role-3-0', type: 'step', style: edgeStyle, zIndex: 10 });
    newEdges.push({ id: 'e-2-3-1', source: 'role-2-0', target: 'role-3-1', type: 'step', style: edgeStyle, zIndex: 10 });
    
    // Right functional node (2-1) to last 3 companies
    newEdges.push({ id: 'e-2-3-2', source: 'role-2-1', target: 'role-3-2', type: 'step', style: edgeStyle, zIndex: 10 });
    newEdges.push({ id: 'e-2-3-3', source: 'role-2-1', target: 'role-3-3', type: 'step', style: edgeStyle, zIndex: 10 });
    newEdges.push({ id: 'e-2-3-4', source: 'role-2-1', target: 'role-3-4', type: 'step', style: edgeStyle, zIndex: 10 });

    return { nodes: newNodes, edges: newEdges };
  }, [layers]);

  return (
    <div className="hsv-pyramid-flow-container">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        minZoom={0.2}
        maxZoom={1.5}
        elementsSelectable={false}
        nodesDraggable={false}
        nodesConnectable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        preventScrolling={false}
        panOnDrag={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
      />
    </div>
  );
}
